import React, { useRef, useState } from 'react';
import { Icon, SelectField } from '@unite-us/ui';
import PropTypes from 'prop-types';
import {
  ColumnHeader,
  DataRow,
  DataCell,
} from 'src/common/tables/V2';
import { Link } from 'react-router';
import { Field, Form } from 'react-final-form';
import { isEmpty } from 'lodash';
import { useBulkUpdate } from 'common/hooks/useBulkUpdate';
import WorkqueueBulkAction from 'common/Workqueue/WorkqueueBulkAction';
import { REQUEST_UNASSIGNED, useAssigneeOptions } from 'common/Workqueue/hooks/useAssigneeOptions';
import { BulkAssignDialogHeader } from './DialogHeaders';

export const BulkAssignEnrollments = ({
  deselectAll,
  deselectRows,
  selectedRows,
  disableSubmitRowsButton,
}) => {
  const [erroredRows, setErroredRows] = useState(false);
  const formRef = useRef();
  const ewqBulkAssignUserFormRef = useRef();
  const assigneeOptions = useAssigneeOptions('enrollments');

  const disableOpenModal = selectedRows.length === 0;

  const closeBulkAssignUserForm = () => {
    // eslint-disable-next-line no-unused-expressions
    ewqBulkAssignUserFormRef?.current?.closeDialog();
    setErroredRows(false);

    if (formRef?.current) {
      // eslint-disable-next-line no-unused-expressions
      formRef?.current?.reset();
    }
  };

  const openBulkAssignUserForm = () => {
    ewqBulkAssignUserFormRef.current.openDialog();
  };

  const bulkAssignUser = useBulkUpdate(
    'enrollment_request',
    selectedRows.map((row) => row.id),
  );

  const submitBulkAssignEnrollments = async (values, form) => {
    const updatedAssignee = values.assignee === REQUEST_UNASSIGNED || !values.assignee ? null : values.assignee;
    const { successfulRequestIds, erroredRequestIds } = await bulkAssignUser(
      { assignee: updatedAssignee },
      'Request successfully assigned.',
    );

    if (!isEmpty(erroredRequestIds)) {
      setErroredRows(true);
      deselectRows(selectedRows.filter(({ id }) => successfulRequestIds?.includes(id)));
    } else {
      closeBulkAssignUserForm();
      deselectAll();
    }
    form.change('assignee', undefined);
  };

  const validateForm = (values) => {
    const errors = {};
    if (!values.assignee) {
      errors.assignee = 'Assignee is required';
    }

    return errors;
  };

  return (
    <Form
      validate={validateForm}
      onSubmit={submitBulkAssignEnrollments}
      render={({
        handleSubmit,
        submitting,
        pristine,
        hasValidationErrors,
        form,
      }) => {
        formRef.current = form;

        return (
          <WorkqueueBulkAction
            action={'Assign'}
            open={openBulkAssignUserForm}
            close={closeBulkAssignUserForm}
            onSubmit={handleSubmit}
            bulkActionIcon="IconUserCircle"
            dialogTitle="Assign a user to selected enrollment requests"
            confirmLabel="Assign User"
            dialogRef={ewqBulkAssignUserFormRef}
            disableOpenModal={disableOpenModal}
            selectedRows={selectedRows}
            erroredRows={erroredRows}
            deselectAll={deselectAll}
            bulkActionIconSize={22}
            disableSubmitRowsButton={disableSubmitRowsButton || submitting || pristine || hasValidationErrors}
          >
            <div className="mb-9">
              <BulkAssignDialogHeader selectedRowCount={selectedRows.length} erroredRows={erroredRows} />
              <form onSubmit={handleSubmit}>
                <div className="space-y-6 mb-8">
                  <Field name="assignee">
                    {({ input, meta }) => (
                      <div>
                        <SelectField
                          label="Assign a User"
                          placeholder="Select an assignee"
                          options={assigneeOptions}
                          className="w-64 ml-3"
                          required
                          invalid={meta.touched && !!meta.error}
                          error={meta.touched && meta.error}
                          position="bottom"
                          {...input}
                        />
                      </div>
                    )}
                  </Field>
                </div>

                <table className="w-full table-fixed">
                  <thead>
                    <tr>
                      <ColumnHeader className="w-[20%] py-2 pr-2 text-left font-medium">Request ID</ColumnHeader>
                      <ColumnHeader className="w-[15%] py-2 pr-2 text-left font-medium">Request Type</ColumnHeader>
                      <ColumnHeader className="w-[25%] py-2 pr-2 text-left font-medium">Client Name</ColumnHeader>
                      <ColumnHeader className="w-[35%] py-2 pr-2 text-left font-medium">
                        Social Care Coverage
                      </ColumnHeader>
                      <ColumnHeader className="w-[5%] py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {
                      selectedRows.map((enrollment) => (
                        <DataRow key={`bulk-assign-enrollment-request-row-${enrollment.short_id}`}>
                          <DataCell className="pt-2 pr-4 align-top">
                            <Link
                              to={`/enrollments/${enrollment.id}`}
                              className="underline"
                            >
                              {enrollment.short_id}
                            </Link>
                          </DataCell>
                          <DataCell className="py-2 pr-4 align-top">
                            {enrollment.request_type === 'enroll' ? 'Enroll' : 'Disenroll'}
                          </DataCell>
                          <DataCell className="py-2 pr-4 align-top truncate">
                            {`${enrollment.person.first_name} ${enrollment.person.last_name}`}
                          </DataCell>
                          <DataCell className="py-2 pr-0 align-top truncate">
                            {enrollment.plan.name}
                          </DataCell>
                          <DataCell className="py-2 text-right align-top">
                            <Icon
                              icon="IconCross"
                              size={10}
                              onClick={() => deselectRows([enrollment])}
                              ariaLabel={`deselect-enrollment-request-${enrollment.short_id}`}
                              className="cursor-pointer"
                            />
                          </DataCell>
                        </DataRow>
                      ))
                    }
                  </tbody>
                </table>
              </form>
            </div>
          </WorkqueueBulkAction>
        );
      }}
    />
  );
};

BulkAssignEnrollments.propTypes = {
  deselectAll: PropTypes.func.isRequired,
  deselectRows: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
  disableSubmitRowsButton: PropTypes.bool.isRequired,
};
